/**
 * Player Display Utilities
 * 
 * Shared utilities for handling player name display and disambiguation
 * in group rounds and other multi-player contexts.
 */

/**
 * Generate unique display names for players with duplicate first names
 * Handles edge cases where players share both first name AND last name initial
 * 
 * Algorithm:
 * 1. Try "First L." (one initial) for duplicates
 * 2. If label collision detected, extend initial (e.g., "Chad Sa." vs "Chad Sm.")
 * 3. If still tied, append numeric index "#1/#2"
 * 
 * @param {Array} players - Array of { player_id, name, handicap }
 * @returns {Object} - Map of player_id to display name
 * 
 * Examples:
 * - ["Chad Sano", "Mark Jones"] -> { id1: "Chad", id2: "Mark" }
 * - ["Chad Sano", "Chad Miller"] -> { id1: "Chad S.", id2: "Chad M." }
 * - ["Chad Sano", "Chad Smith"] -> { id1: "Chad Sa.", id2: "Chad Sm." }
 * - ["Chad Sano", "Chad San"] -> { id1: "Chad San.", id2: "Chad San #2" }
 * - ["Chad", "Chad"] -> { id1: "Chad #1", id2: "Chad #2" }
 */
export const getUniqueDisplayNames = (players) => {
  if (!players || players.length === 0) return {};
  
  const displayNames = {};
  
  // Group players by first name
  const firstNameGroups = {};
  players.forEach(p => {
    const firstName = (p.name || 'Player').split(' ')[0];
    if (!firstNameGroups[firstName]) {
      firstNameGroups[firstName] = [];
    }
    firstNameGroups[firstName].push(p);
  });
  
  // Process each group
  Object.entries(firstNameGroups).forEach(([firstName, group]) => {
    if (group.length === 1) {
      // Unique first name - just use it
      displayNames[group[0].player_id] = firstName;
    } else {
      // Multiple players with same first name - need disambiguation
      disambiguateGroup(firstName, group, displayNames);
    }
  });
  
  return displayNames;
};

/**
 * Disambiguate a group of players with the same first name
 * @param {string} firstName - The shared first name
 * @param {Array} group - Array of player objects sharing this first name
 * @param {Object} displayNames - Output map to populate
 */
function disambiguateGroup(firstName, group, displayNames) {
  // Step 1: Try single initial (e.g., "Chad S.")
  const initialLabels = {};
  
  group.forEach(p => {
    const fullName = p.name || 'Player';
    const nameParts = fullName.split(' ');
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    
    if (lastName) {
      const label = `${firstName} ${lastName[0]}.`;
      if (!initialLabels[label]) {
        initialLabels[label] = [];
      }
      initialLabels[label].push({ player: p, lastName });
    } else {
      // No last name - will need index fallback
      if (!initialLabels['__NO_LAST_NAME__']) {
        initialLabels['__NO_LAST_NAME__'] = [];
      }
      initialLabels['__NO_LAST_NAME__'].push({ player: p, lastName: '' });
    }
  });
  
  // Process each label group
  Object.entries(initialLabels).forEach(([label, labelGroup]) => {
    if (label === '__NO_LAST_NAME__') {
      // No last names - use numeric index
      labelGroup.forEach((item, idx) => {
        displayNames[item.player.player_id] = `${firstName} #${idx + 1}`;
      });
    } else if (labelGroup.length === 1) {
      // Unique single-initial label
      displayNames[labelGroup[0].player.player_id] = label;
    } else {
      // Collision on single initial - try extending
      extendInitials(firstName, labelGroup, displayNames);
    }
  });
}

/**
 * Extend initials when single initial causes collision
 * e.g., "Chad S." collision -> "Chad Sa." vs "Chad Sm."
 */
function extendInitials(firstName, labelGroup, displayNames) {
  // Find minimum unique prefix for each last name
  const lastNames = labelGroup.map(item => item.lastName);
  
  // Try progressively longer prefixes
  for (let prefixLen = 2; prefixLen <= Math.max(...lastNames.map(ln => ln.length)); prefixLen++) {
    const prefixLabels = {};
    let allUnique = true;
    
    labelGroup.forEach(item => {
      const prefix = item.lastName.substring(0, prefixLen);
      const label = `${firstName} ${prefix}.`;
      
      if (prefixLabels[label]) {
        allUnique = false;
      } else {
        prefixLabels[label] = item;
      }
    });
    
    if (allUnique) {
      // Found unique prefixes
      labelGroup.forEach(item => {
        const prefix = item.lastName.substring(0, prefixLen);
        displayNames[item.player.player_id] = `${firstName} ${prefix}.`;
      });
      return;
    }
  }
  
  // Fallback: use full last name + index if still colliding
  const seenLabels = {};
  labelGroup.forEach(item => {
    let label = `${firstName} ${item.lastName}`;
    if (seenLabels[label]) {
      seenLabels[label]++;
      label = `${label} #${seenLabels[label]}`;
    } else {
      seenLabels[label] = 1;
    }
    displayNames[item.player.player_id] = label;
  });
}

/**
 * Get a shorter display name for tight UI spaces (e.g., score boxes)
 * @param {string} displayName - The full disambiguated display name
 * @returns {string} - Shortened name (max ~10 chars)
 */
export const getShortDisplayName = (displayName) => {
  if (!displayName) return 'P';
  if (displayName.length <= 10) return displayName;
  
  // If it has a space (like "Chad S."), keep it
  if (displayName.includes(' ')) {
    return displayName;
  }
  
  // Truncate long single names
  return displayName.substring(0, 8) + '..';
};

export default getUniqueDisplayNames;
